import { useMemo } from 'react';
import { ProjectStatsResponse } from '../../../../shared/services/monitor/keywords/stats/MonitorPromptsKeywordsStatsResponse';
import { MonitorPrompt } from '../../../../shared/models/monitor/MonitorPrompt';
import { ChatType } from '../../../../shared/types/monitor/ChatType';
import { AccountResponse } from '../../../../shared/models/account/AccountResponse';
import { useProjectData } from './UseProjectData';
import { KeywordResponse } from '../../../../shared/models/monitor/KeywordResponse';
import { findTagIdInKeyword } from '../../../../shared/helpers/keyword/KeywordHelper';
import { getTagLabel } from '../../../../shared/helpers/tags/TagsHelper';
import { TagsResponse } from '../../../../shared/models/tags/TagsResponse';
import { buildAllowedChatTypes, expandSelectedChatTypes } from '../../../../shared/helpers/MonitorTypeHelpers';

export const usePromptsData = (
  isKeywordDailyStatsForProjectLoading: boolean,
  isEventsLoading: boolean,
  keywordsDailyStatsForProject: ProjectStatsResponse,
  keywordsMap: Map<string, string>,
  monitorPrompts: MonitorPrompt[] | null,
  selectedChatType: ChatType | null,
  accountResponse: AccountResponse | null,
  commonKeywords: KeywordResponse[],
  tags: TagsResponse[],
  selectedChatTypes?: ChatType[]
) => {
  const { prepareAndSetPromptsData } = useProjectData();
  return useMemo(() => {
    if (
      isKeywordDailyStatsForProjectLoading ||
      isEventsLoading ||
      !keywordsDailyStatsForProject ||
      keywordsMap.size === 0 ||
      !monitorPrompts ||
      !commonKeywords ||
      !tags
    ) {
      return null;
    }

    let allowedChatTypes: Set<string> | undefined;

    if (accountResponse?.features) {
      const accountAllowed = buildAllowedChatTypes(accountResponse.features);

      if (selectedChatTypes && selectedChatTypes.length > 0) {
        const selectedExpanded = expandSelectedChatTypes(selectedChatTypes);
        allowedChatTypes = new Set(Array.from(accountAllowed).filter((ct) => selectedExpanded.has(ct)));
      } else {
        allowedChatTypes = accountAllowed;
      }
    }

    const data = prepareAndSetPromptsData(
      monitorPrompts,
      keywordsDailyStatsForProject.monitorPromptsKeywordsStatsResponse,
      selectedChatType || 'GPT',
      allowedChatTypes
    );

    return data.map((prompt) => {
      const tagId = findTagIdInKeyword(prompt.keyword, commonKeywords);
      const keyword = getTagLabel(tags, tagId, prompt.keyword);
      return {
        ...prompt,
        keyword: keywordsMap.get(keyword) || keyword,
        isRequiredAttention: !!(accountResponse?.isSuperAdmin && prompt.isRequiredAttention),
      };
    });
  }, [
    keywordsDailyStatsForProject,
    isEventsLoading,
    monitorPrompts,
    selectedChatType,
    selectedChatTypes,
    keywordsMap,
    accountResponse,
    commonKeywords,
    tags,
  ]);
};
